import { decryptField, encryptField } from '../crypto/aesgcm';
import { SandboxSession, WrongPasswordError } from '../sandbox/session';
import type { CruzFindVault } from '../db/schema';
import { computeAnonToken } from './anonToken';
import { checkSubscriptionStatus, type SubscriptionStatus } from './boxC';

export class NotAuthenticatedError extends Error {
  constructor() {
    super('Session is locked — sign in before performing this operation');
    this.name = 'NotAuthenticatedError';
  }
}

export { WrongPasswordError };

export interface AuthenticatedIdentity {
  email: string;
  anonToken: string;
  subscription: SubscriptionStatus;
}

export class AuthService {
  constructor(
    private readonly session: SandboxSession,
    private readonly db: CruzFindVault,
  ) {}

  async vaultExists(): Promise<boolean> {
    return this.session.isInitialized();
  }

  async signUp(email: string, password: string): Promise<AuthenticatedIdentity> {
    if (!email.trim()) throw new Error('Email cannot be empty');
    if (password.length < 8) throw new Error('Password must be at least 8 characters');

    await this.session.initialize(password);
    return this.completeAuth(email);
  }

  async signIn(password: string): Promise<AuthenticatedIdentity> {
    await this.session.unlock(password);
    const meta = await this.db.vaultMeta.get('singleton');
    if (!meta?.encryptedEmail) {
      // Vault was initialised by an older flow that didn't capture an email.
      // Returning a placeholder keeps the contract; the UI may prompt to add one.
      return this.completeAuth('(no email on file)', { skipPersist: true });
    }
    const state = this.session.getState();
    if (state.kind !== 'unlocked') throw new NotAuthenticatedError();
    const email = await decryptField(state.keys.aesKey, meta.encryptedEmail);
    return this.completeAuth(email, { skipPersist: true });
  }

  signOut(): void {
    this.session.lock();
  }

  private async completeAuth(
    email: string,
    opts: { skipPersist?: boolean } = {},
  ): Promise<AuthenticatedIdentity> {
    const state = this.session.getState();
    if (state.kind !== 'unlocked') throw new NotAuthenticatedError();

    if (!opts.skipPersist) {
      const encryptedEmail = await encryptField(state.keys.aesKey, email);
      await this.db.vaultMeta.update('singleton', { encryptedEmail });
    }

    const anonToken = await computeAnonToken(state.keys.anonIdKey);
    const subscription = await checkSubscriptionStatus(anonToken);
    return { email, anonToken, subscription };
  }
}
